/**
 * Magic-link landing — SPEC §11.2, §11.6.
 *
 * The link arrives as a GET navigation from a mail client, but the token is exchanged with
 * a POST so it never sits in a Referer header or a server access log.
 *
 * This screen must work even when the code is locked out: it is SPEC §11.6 path 1, the
 * recovery route that needs no human. `verifyLink` on the server ignores the lockout
 * deliberately, and this page must never add a check of its own.
 */

import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

const SUPPORT_EMAIL = 'admin@gearupwithcpr.com';

export function VerifyLink() {
  const { refresh } = useSession();
  const [error, setError] = useState<string | null>(null);
  // Mail scanners and React's development double-invoke can both fire this twice, and the
  // token is single-use — the second attempt would fail as "already used" and show an
  // error to someone who is, in fact, signed in.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('That sign-in link is not valid.');
      return;
    }

    void (async () => {
      try {
        await api.signIn.verifyLink(token, true);
        // Drop the token from the address bar before anything else can read it.
        window.history.replaceState({}, '', '/');
        await refresh();
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : 'That sign-in link could not be used.',
        );
      }
    })();
  }, [refresh]);

  if (!error) return <p className="muted center">Signing you in…</p>;

  return (
    <div className="card stack">
      <h1>That link didn't work</h1>
      <div className="notice notice--error" role="alert">
        <span className="notice__icon" aria-hidden="true">!</span>
        <span>{error}</span>
      </div>
      <p className="muted">
        Links expire after 10 minutes and can only be used once. Requesting a new one takes
        a few seconds.
      </p>
      <a className="btn btn--primary btn--block" href="/">
        Get a new link
      </a>
      <p className="small muted">
        Still stuck? Email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--navy)' }}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}
